import { useCallback, useEffect, useRef, useState } from 'react'

import { DatePicker } from '../../lib/DatePicker'
import { monthRangeThai } from '../../lib/datetime'
import { errorText } from '../../lib/offsite'
import { Pager } from '../../lib/Pager'
import { useSession } from '../../lib/SessionContext'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { SectionHead } from '../offsite/parts'

/* ============================================================================
   ข้อมูลดิบจากเครื่องตอกบัตร — HR_TA_DATA (อ่านอย่างเดียว)
   API: backend/src/routes/adminTaData.js · lib/taData.js

   ── ทำไมต้องมีหน้านี้ ──────────────────────────────────────────────────────
   ตารางนี้คือ **ต้นทางของเวลาเข้า–ออกทั้งระบบ** ทุกแถวคือการแตะบัตร 1 ครั้ง
   โดยยังไม่ตีความว่าเข้าหรือออก — HR_EMP_ACTUAL กับ HR_EMP_WORK คิดต่อจากตรงนี้

   เวลาใครบอกว่า "ตอกแล้วแต่ระบบบอกขาดงาน" คำถามแรกคือ *เครื่องรับบัตรไปจริงไหม*
   ซึ่งตอบได้จากตารางนี้ที่เดียว ที่ผ่านมาต้องเขียน SQL ถามเอง

   ── สิ่งที่หน้านี้ตั้งใจให้เห็น ─────────────────────────────────────────────
   1. บัตรที่ไม่มีเจ้าของใน Users (บัตรกำพร้า) — คนตอกทุกวันแต่ระบบไม่รู้ว่าใคร
      ของจริงเดือน ต.ค. 2026 มี 372 รอยจาก 15 ใบ ที่ไม่ผูกกับพนักงานคนไหนเลย
      ถ้า join แบบ INNER แถวพวกนี้จะหายเงียบ ๆ จึงใช้ LEFT JOIN แล้วติดป้ายแทน
   2. แถวที่ถูกตัดออกจากการคำนวณ (CANCEL_REC = 'O'/'X') — มีจริง 32,236 แถวทั้งตาราง

   ── ⚠️ ทำไมบังคับเลือกช่วงวัน ───────────────────────────────────────────────
   ตาราง 783,395 แถว และมี index ตัวเดียวคือ PK (MC_Card, TA_Time)
   **ไม่มี index ที่ WorkDate** → ไม่ใส่ช่วงวันคือ scan ทั้งตาราง
   backend จึงบังคับและจำกัดไม่เกิน 92 วันต่อครั้ง (ดู MAX_RANGE_DAYS)
   ============================================================================ */

type Punch = {
  seq: number
  card: string
  workDate: string | null
  /** 'YYYY-MM-DD HH:mm' */
  taTime: string | null
  createDate: string | null
  cancelRec: string | null
  /** true = เครื่องคิดเวลาไม่นับแถวนี้ */
  cancelled: boolean
  seqOrder: number | null
  calWorkDate: string | null
  empCode: string | null
  empId: string | null
  empName: string | null
  departCode: string | null
  companyCode: string | null
  /** true = ไม่มีเจ้าของบัตรใน Users */
  orphan: boolean
}

type PunchPage = {
  punches: Punch[]
  total: number
  page: number
  pageSize: number
  range: { from: string; to: string; days: number }
}

type Summary = {
  total: number
  cards: number
  cancelled: number
  orphan: number
}

type ImportResult = {
  applied: boolean
  total: number
  inserted: number
  alreadyThere: number
  duplicatesInFile: number
  skipped: { line: number; text: string; reason: string }[]
  range: { from: string; to: string } | null
  unknownCards: string[]
  fileName: string | null
}

const BASE = '/api/admin/ta-data'
const nf = new Intl.NumberFormat('th-TH')

export default function TaData() {
  const { call } = useSession()

  const [rows, setRows] = useState<PunchPage | null>(null)
  const [sum, setSum] = useState<Summary | null>(null)
  const [page, setPage] = useState(1)
  /* 25 ไม่ใช่ 10 เพราะหน้านี้ใช้กวาดสายตาหาแถวที่ผิดปกติ ไม่ได้อ่านทีละแถว
     (ต้องเป็นค่าที่มีใน sizes ของ Pager ไม่งั้น dropdown จะไม่ตรงกับที่โชว์อยู่) */
  const [pageSize, setPageSize] = useState(25)
  const sort = useSort([{ key: 'time', dir: 'desc' }], () => setPage(1))

  /* ค่าเริ่มต้น = เดือนปัจจุบัน เหมือนหน้าอื่นในหมวดรายงาน
     คนเปิดหน้านี้มักถามถึงรอบเดือน ไม่ใช่เฉพาะวันนี้ */
  const [range] = useState(() => monthRangeThai())
  const [from, setFrom] = useState(range.from)
  const [to, setTo] = useState(range.to)
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [orphanOnly, setOrphanOnly] = useState(false)
  const [hideCancelled, setHideCancelled] = useState(false)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* ── นำเข้าไฟล์ .dat ──────────────────────────────────────────────────────
     สองจังหวะเสมอ: อ่านไฟล์แล้วโชว์ผลก่อน (preview) → คนดูแล้วค่อยกดยืนยัน
     เพราะเขียนลงตารางที่เป็นต้นทางของเวลาทำงานทั้งระบบ กดพลาดแล้วตามลบยาก */
  const fileRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [importFrom, setImportFrom] = useState('')
  const [importTo, setImportTo] = useState('')
  const [preview, setPreview] = useState<ImportResult | null>(null)
  const [done, setDone] = useState<ImportResult | null>(null)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        from,
        to,
      })
      if (q) qs.set('q', q)
      if (orphanOnly) qs.set('orphan', '1')
      if (hideCancelled) qs.set('cancelled', '0')
      if (sort.param) qs.set('sort', sort.param)

      /* ตัวเลขสรุปคิดจากทั้งช่วง ไม่ใช่จากหน้าที่โหลด — ยิงคู่กันไปเลย
         ถ้าสรุปล้ม ไม่ให้ทั้งหน้าพัง เพราะตารางยังอ่านได้ */
      const [list, s] = await Promise.all([
        call<PunchPage>(`${BASE}?${qs}`),
        call<Summary>(`${BASE}/summary?${new URLSearchParams({ from, to })}`).catch(() => null),
      ])
      setRows(list)
      setSum(s)
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setRows(null)
    } finally {
      setLoading(false)
    }
  }, [call, page, pageSize, from, to, q, orphanOnly, hideCancelled, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  const resetImport = () => {
    setFileName(null)
    setContent(null)
    setPreview(null)
    setDone(null)
    setImportError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const onFileChange = (file: File | null) => {
    setPreview(null)
    setDone(null)
    setImportError(null)
    if (!file) {
      setFileName(null)
      setContent(null)
      return
    }
    setFileName(file.name)
    setContent(null)
  }

  const doPreview = async () => {
    const file = fileRef.current?.files?.[0]
    if (!file) return
    setPreview(null)
    setDone(null)
    setImportError(null)
    setImporting(true)
    try {
      const text = content ?? (await file.text())
      setContent(text)
      setPreview(
        await call<ImportResult>(`${BASE}/import`, {
          method: 'POST',
          body: JSON.stringify({
            fileName: file.name,
            content: text,
            dateFrom: importFrom || undefined,
            dateTo: importTo || undefined,
          }),
        }),
      )
    } catch (err) {
      setImportError(errorText(err))
    } finally {
      setImporting(false)
    }
  }

  const confirmImport = async () => {
    if (!content) return
    setImporting(true)
    setImportError(null)
    try {
      const r = await call<ImportResult>(`${BASE}/import`, {
        method: 'POST',
        body: JSON.stringify({
          fileName,
          content,
          apply: true,
          dateFrom: importFrom || undefined,
          dateTo: importTo || undefined,
        }),
      })
      setDone(r)
      setPreview(null)
      setContent(null)
      if (fileRef.current) fileRef.current.value = ''
      // ตารางด้านล่างต้องเห็นแถวใหม่ทันที ไม่ต้องให้คนกดรีเฟรชเอง
      await load()
    } catch (err) {
      setImportError(errorText(err))
    } finally {
      setImporting(false)
    }
  }

  const list = rows?.punches ?? []

  return (
    <>
      <h1>ข้อมูลดิบเครื่องตอกบัตร</h1>
      <p className="meta">
        ทุกแถวคือการแตะบัตร 1 ครั้ง ตามที่เครื่องบันทึกไว้ <strong>ยังไม่ตีความว่าเข้าหรือออก</strong> —
        เวลาเข้า–ออกที่เห็นในหน้าอื่นคิดต่อจากตารางนี้ ถ้าสงสัยว่าตอกแล้วระบบไม่เห็น ให้ดูที่นี่ก่อน
      </p>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      <section className="card">
        {/* ไม่ใช้ SectionHead เพราะมันบังคับให้มีปุ่ม "โหลดใหม่"
            ส่วนนี้ไม่มีอะไรให้โหลด — ปุ่มที่กดแล้วไม่เกิดอะไรแย่กว่าไม่มีปุ่ม */}
        <h2>นำเข้าไฟล์จากเครื่องตอกบัตร</h2>

        <p className="meta">
          ไฟล์ <code>.dat</code> ที่ดึงจากเครื่อง — แทนโปรแกรมเดิมที่ต้องเปิดบนเครื่อง HR
          · ระบบ<strong>เพิ่มแถวใหม่อย่างเดียว</strong> แถวที่มีอยู่แล้วจะถูกข้าม ไม่เขียนทับของเดิม
        </p>

        <form className="toolbar" onSubmit={(e) => e.preventDefault()}>
          <label>
            เลือกไฟล์
            <input
              ref={fileRef}
              type="file"
              accept=".dat,.txt"
              disabled={importing}
              onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
            />
          </label>
          <label>
            ตั้งแต่วันที่
            <DatePicker value={importFrom} onChange={(v) => setImportFrom(v)} />
          </label>
          <label>
            ถึงวันที่
            <DatePicker value={importTo} onChange={(v) => setImportTo(v)} min={importFrom} />
          </label>
          <button
            type="button"
            onClick={() => void doPreview()}
            disabled={importing || !fileName}
          >
            นำเข้า
          </button>
          {(preview || done || importError) && (
            <button type="button" className="ghost" onClick={resetImport} disabled={importing}>
              ล้าง
            </button>
          )}
        </form>

        {importing && <p className="meta">{preview ? 'กำลังนำเข้า…' : 'กำลังอ่านไฟล์…'}</p>}
        {importError && <p className="error">นำเข้าไม่สำเร็จ — {importError}</p>}

        {/* ขั้นดูผลก่อน — ยังไม่เขียนอะไรลงฐานข้อมูล */}
        {preview && (
          <>
            <p className="meta">
              อ่านไฟล์ <strong>{fileName}</strong> ได้ {nf.format(preview.total)} รอย
              {preview.range && (
                <>
                  {' '}
                  ช่วงวันที่ {preview.range.from}
                  {preview.range.to !== preview.range.from && <> ถึง {preview.range.to}</>}
                </>
              )}
            </p>

            <p className="meta">
              จะเพิ่มใหม่ <strong>{nf.format(preview.total - preview.alreadyThere)}</strong> รอย ·
              มีอยู่แล้วในระบบ {nf.format(preview.alreadyThere)} รอย (จะถูกข้าม)
              {preview.duplicatesInFile > 0 && (
                <> · ซ้ำกันเองในไฟล์ {nf.format(preview.duplicatesInFile)} รอย (นับครั้งเดียว)</>
              )}
            </p>

            {/* บรรทัดที่อ่านไม่ได้ต้องเห็น — โปรแกรมเดิมทิ้งเงียบ ๆ ไม่บอกใคร */}
            {preview.skipped.length > 0 && (
              <>
                <p className="warn">
                  มี <strong>{preview.skipped.length} บรรทัด</strong>ที่อ่านไม่ได้ และจะไม่ถูกนำเข้า —
                  ถ้าเยอะผิดปกติ ให้ตรวจว่าเลือกไฟล์ถูกอันไหม
                </p>
                <div className="table-wrap table-wrap--rows10">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>บรรทัดที่</th>
                        <th>เหตุผล</th>
                        <th>เนื้อบรรทัด</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.skipped.map((s) => (
                        <tr key={s.line}>
                          <td className="nowrap">{s.line}</td>
                          <td className="nowrap">{s.reason}</td>
                          <td>
                            <code>{s.text}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {preview.unknownCards.length > 0 && (
              <p className="warn">
                มีบัตร <strong>{preview.unknownCards.length} ใบ</strong>ที่ยังไม่ได้ผูกกับพนักงานคนไหน —
                นำเข้าได้ตามปกติ แต่รอยพวกนี้จะไม่ถูกนับเป็นเวลาทำงานของใครจนกว่าจะผูกบัตรให้ถูกคน
                <br />
                <code>{preview.unknownCards.slice(0, 20).join(', ')}</code>
                {preview.unknownCards.length > 20 && <> และอีก {preview.unknownCards.length - 20} ใบ</>}
              </p>
            )}

            {preview.total - preview.alreadyThere === 0 ? (
              <p className="meta">ทุกรอยในไฟล์นี้มีอยู่ในระบบแล้ว ไม่มีอะไรต้องนำเข้า</p>
            ) : (
              <p>
                <button type="button" onClick={() => void confirmImport()} disabled={importing}>
                  {importing ? 'กำลังนำเข้า…' : `ยืนยันนำเข้า ${nf.format(preview.total - preview.alreadyThere)} รอย`}
                </button>
              </p>
            )}
          </>
        )}

        {done && (
          <p className="ok">
            นำเข้าเรียบร้อย — เพิ่ม <strong>{nf.format(done.inserted)}</strong> รอย
            {done.alreadyThere > 0 && <> · ข้ามที่มีอยู่แล้ว {nf.format(done.alreadyThere)} รอย</>}
          </p>
        )}
      </section>

      <section className="card">
        <SectionHead
          title="รอยตอกบัตร"
          meta={rows ? `${nf.format(rows.total)} รายการ` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(term.trim())
          }}
        >
          <label>
            ตั้งแต่วันที่
            <DatePicker
              value={from}
              onChange={(v) => {
                setPage(1)
                setFrom(v)
              }}
            />
          </label>
          <label>
            ถึงวันที่
            <DatePicker
              value={to}
              onChange={(v) => {
                setPage(1)
                setTo(v)
              }}
              min={from}
            />
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน ชื่อ หรือเลขบัตร"
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={orphanOnly}
              onChange={(e) => {
                setPage(1)
                setOrphanOnly(e.target.checked)
              }}
            />
            เฉพาะบัตรที่ไม่รู้จัก
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={hideCancelled}
              onChange={(e) => {
                setPage(1)
                setHideCancelled(e.target.checked)
              }}
            />
            ซ่อนแถวที่ถูกตัดออก
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
        </form>

        {/* สรุปของทั้งช่วง ไม่ใช่ของหน้านี้ — บอกไว้ให้ชัดกันอ่านผิด */}
        {sum && (
          <p className="meta">
            ช่วงที่เลือกมี <strong>{nf.format(sum.total)}</strong> รอย จากบัตร{' '}
            <strong>{nf.format(sum.cards)}</strong> ใบ
            {sum.orphan > 0 && (
              <>
                {' · '}
                <strong className="bad">{nf.format(sum.orphan)}</strong> รอยมาจากบัตรที่ไม่มีเจ้าของ
              </>
            )}
            {sum.cancelled > 0 && <> · ถูกตัดออกจากการคำนวณ {nf.format(sum.cancelled)} รอย</>}
          </p>
        )}

        {/* บัตรกำพร้า = มีคนตอกอยู่จริงแต่ระบบไม่รู้ว่าใคร เวลาทำงานของคนนั้นจะหายไปทั้งก้อน */}
        {sum !== null && sum.orphan > 0 && !orphanOnly && (
          <p className="warn">
            มี <strong>{nf.format(sum.orphan)} รอย</strong>ที่ตอกด้วยบัตรซึ่งไม่ได้ผูกกับพนักงานคนไหน —
            รอยพวกนี้จะไม่ถูกนับเป็นเวลาทำงานของใครเลย ติ๊ก “เฉพาะบัตรที่ไม่รู้จัก” เพื่อดูว่าเป็นใบไหนบ้าง
            แล้วไปผูกเลขบัตรให้ถูกคนที่หน้าข้อมูลพนักงาน
          </p>
        )}

        <SortBar
          sort={sort.sort}
          labels={{
            time: 'เวลาที่ตอก',
            card: 'เลขบัตร',
            empCode: 'รหัสพนักงาน',
            empName: 'ชื่อ',
            workDate: 'วันที่',
          }}
          onClear={() => sort.setSort([])}
        />

        {rows === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : list.length === 0 ? (
          <p className="meta">
            ไม่มีรอยตอกบัตรในช่วงวันที่เลือก
            {orphanOnly && ' (เฉพาะบัตรที่ไม่รู้จัก)'}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <SortTh
                    label="เวลาที่ตอก"
                    sortKey="time"
                    sort={sort.sort}
                    onToggle={sort.toggle}
                    className="nowrap"
                  />
                  <SortTh label="เลขบัตร" sortKey="card" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="พนักงาน" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh
                    label="วันที่ของรอบงาน"
                    sortKey="workDate"
                    sort={sort.sort}
                    onToggle={sort.toggle}
                    className="nowrap"
                  />
                  <th className="nowrap">บันทึกเข้าระบบ</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.seq} className={p.cancelled ? 'off' : undefined}>
                    <td className="nowrap">{p.taTime ?? '-'}</td>
                    <td className="nowrap">{p.card}</td>
                    <td>
                      {p.orphan ? (
                        <span className="bad">ไม่รู้จักบัตรนี้</span>
                      ) : (
                        <>
                          <strong>{p.empCode}</strong>
                          <div className="meta">{p.empName ?? '-'}</div>
                        </>
                      )}
                    </td>
                    <td className="nowrap">
                      {p.workDate ?? '-'}
                      {/* cal_workdate ต่างจาก WorkDate เมื่อรอยนี้ถูกจับให้เป็นของอีกวัน
                          (กะข้ามคืน) — ต่างเมื่อไรต้องเห็น ไม่งั้นงงว่าทำไมไปโผล่วันอื่น */}
                      {p.calWorkDate && p.calWorkDate !== p.workDate && (
                        <div className="meta">นับเป็นวันที่ {p.calWorkDate}</div>
                      )}
                    </td>
                    <td className="nowrap meta">{p.createDate ?? '-'}</td>
                    <td>
                      {p.cancelled ? (
                        <span className="bad">ตัดออก ({p.cancelRec})</span>
                      ) : (
                        <span className="meta">ใช้งาน</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows !== null && rows.total > 0 && (
          <Pager
            page={rows.page}
            pageSize={rows.pageSize}
            total={rows.total}
            loading={loading}
            onPage={setPage}
            onPageSize={(n) => {
              setPage(1)
              setPageSize(n)
            }}
          />
        )}
      </section>
    </>
  )
}
